import { useId, useState } from 'react'
import axios from 'axios'
import {
  Alert,
  AlertContent,
  AlertDescription,
  AlertIndicator,
  AlertTitle,
  Backdrop,
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldHint,
  FieldLabel,
  Input,
  OverlayWrapper,
} from '@firstbite/ui'

import {
  useAgentPreflight,
  useCreateCampaignColumn,
  useListCampaignColumns,
  useListAgentTypes,
} from 'services/campaignColumns/hooks'
import type { AgentPreflight } from 'services/campaignColumns/types'

import { AgentTypeSelect } from './AgentTypeSelect'
import {
  MANUAL_COLUMN_AGENT_TYPE,
  MANUAL_COLUMN_META,
} from './agentTypeMetadata'
import { SettingsForm } from './SettingsForm'

interface Props {
  campaignId: number
  campaignProductId?: number | null
  onClose: () => void
}

const RESERVED_TEMPLATE_VARIABLES = new Set([
  'first_name',
  'last_name',
  'email',
  'company',
  'title',
])

function slugifyColumnName(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')
  const variableSlug = /^\d/.test(slug) ? `column_${slug}` : slug
  return variableSlug.slice(0, 64).replace(/_+$/g, '')
}

function getCreateColumnErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data
    if (data && typeof data === 'object') {
      const detail = (data as { detail?: unknown; error_message?: unknown })
        .detail
      if (typeof detail === 'string' && detail) return detail
      const errorMessage = (
        data as { detail?: unknown; error_message?: unknown }
      ).error_message
      if (typeof errorMessage === 'string' && errorMessage) {
        return errorMessage
      }
    }
  }
  return error instanceof Error ? error.message : 'Failed to create column'
}

export function AddAgentColumnModal({
  campaignId,
  campaignProductId = null,
  onClose,
}: Props) {
  const [selectedAgentType, setSelectedAgentType] = useState<string | null>(
    null
  )
  const [name, setName] = useState('')
  const [nameTouched, setNameTouched] = useState(false)
  const [settings, setSettings] = useState<Record<string, unknown>>({})
  const [error, setError] = useState<string | null>(null)
  const columnNameInputId = useId()

  const { data: agentTypesData } = useListAgentTypes(campaignId)
  const { data: columnsData } = useListCampaignColumns(campaignId)
  const createColumn = useCreateCampaignColumn(campaignId)

  // Predefined agents only — the free-form custom agent path is parked for MVP
  // (backend `custom_prompt` engine still exists; just no UI surface).
  const agentTypes = [
    MANUAL_COLUMN_META,
    ...(agentTypesData?.results ?? []).filter((a) => !a.is_custom),
  ]
  const selectedAgent =
    agentTypes.find((a) => a.agent_type === selectedAgentType) ?? null
  const isManualColumn = selectedAgentType === MANUAL_COLUMN_AGENT_TYPE
  const selectedProductIds = selectedAgent
    ? selectedAgent.settings_schema.flatMap((field) => {
        if (field.type !== 'multiselect_products') return []
        const value = settings[field.key]
        return Array.isArray(value)
          ? value.filter((id): id is number => typeof id === 'number')
          : []
      })
    : []
  const preflightAgentType = isManualColumn ? null : selectedAgentType
  const { data: preflightData, isFetching: isFetchingPreflight } =
    useAgentPreflight(campaignId, preflightAgentType, selectedProductIds)
  const existingSlugs = new Set(
    (columnsData?.results ?? []).map((column) => column.merge_variable_slug)
  )
  const proposedSlug = slugifyColumnName(name)
  const reservedSlug = proposedSlug
    ? RESERVED_TEMPLATE_VARIABLES.has(proposedSlug)
    : false
  const duplicateSlug = proposedSlug ? existingSlugs.has(proposedSlug) : false

  const buildInitialSettings = (
    settingsSchema: NonNullable<typeof selectedAgent>['settings_schema']
  ) => {
    if (!campaignProductId) return {}

    return settingsSchema.reduce<Record<string, unknown>>((acc, field) => {
      if (field.type === 'multiselect_products') {
        acc[field.key] = [campaignProductId]
      }
      return acc
    }, {})
  }

  const handleAgentSelect = (agentType: string | null) => {
    setSelectedAgentType(agentType)
    // Cleared selection — collapse the settings back to empty.
    if (!agentType) {
      setSettings({})
      return
    }
    const meta = agentTypes.find((a) => a.agent_type === agentType)
    if (meta && !nameTouched) {
      setName(meta.name)
    }
    setSettings(meta ? buildInitialSettings(meta.settings_schema) : {})
  }

  const canSave = (() => {
    if (!selectedAgent) return false
    if (!name.trim()) return false
    if (!proposedSlug || reservedSlug || duplicateSlug) return false
    return selectedAgent.settings_schema
      .filter((f) => f.required)
      .every((f) => {
        const v = settings[f.key]
        if (Array.isArray(v)) return v.length > 0
        if (typeof v === 'string') return v.trim().length > 0
        return v !== undefined && v !== null
      })
  })()

  const handleCreate = async () => {
    if (!selectedAgent) return
    setError(null)
    try {
      await createColumn.mutateAsync({
        agent_type: selectedAgent.agent_type,
        name,
        settings,
      })
      onClose()
    } catch (e) {
      setError(getCreateColumnErrorMessage(e))
    }
  }

  return (
    <OverlayWrapper
      isOpen
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <Backdrop className="max-w-2xl">
        <Dialog>
          <DialogHeader>
            <DialogTitle>Add Column</DialogTitle>
            <DialogDescription>
              Select a column type. AI columns preview on the first 5 rows, then
              you approve to run on all; manual columns start empty for you to
              fill in per contact.
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="space-y-4">
            <AgentTypeSelect
              agentTypes={agentTypes}
              selected={selectedAgentType}
              onSelect={handleAgentSelect}
            />

            {selectedAgent && (
              <>
                {isManualColumn ? (
                  <Alert status="default" inline>
                    <AlertIndicator />
                    <AlertContent>
                      <AlertDescription>
                        This column starts empty. Enter a value for each contact
                        before using this variable in an email.
                      </AlertDescription>
                    </AlertContent>
                  </Alert>
                ) : (
                  <AgentPreflightNotice
                    preflight={preflightData}
                    isLoading={isFetchingPreflight}
                  />
                )}
                <Field
                  status={
                    !proposedSlug || reservedSlug || duplicateSlug
                      ? 'error'
                      : 'default'
                  }
                >
                  <FieldLabel htmlFor={columnNameInputId}>
                    Column Name
                  </FieldLabel>
                  <Input
                    id={columnNameInputId}
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value)
                      setNameTouched(true)
                    }}
                  />
                  <ColumnVariableHint
                    slug={proposedSlug}
                    isReserved={reservedSlug}
                    isDuplicate={duplicateSlug}
                  />
                </Field>
                {!isManualColumn && (
                  <SettingsForm
                    agent={selectedAgent}
                    settings={settings}
                    onChange={setSettings}
                    campaignProductId={campaignProductId}
                  />
                )}
              </>
            )}

            {error && (
              <Alert status="error" inline>
                <AlertIndicator />
                <AlertContent>
                  <AlertDescription>{error}</AlertDescription>
                </AlertContent>
              </Alert>
            )}
          </DialogBody>

          <DialogFooter>
            <DialogClose appearance="outline">Cancel</DialogClose>
            <Button
              variant="primary"
              onClick={handleCreate}
              loading={createColumn.isPending}
              disabled={!canSave}
            >
              {isManualColumn ? 'Create Column' : 'Create & Preview'}
            </Button>
          </DialogFooter>
        </Dialog>
      </Backdrop>
    </OverlayWrapper>
  )
}

function ColumnVariableHint({
  slug,
  isReserved,
  isDuplicate,
}: {
  slug: string
  isReserved: boolean
  isDuplicate: boolean
}) {
  // These three states each block submission (canSave gates on the same
  // conditions), so they're errors, not warnings. FieldHint inherits the
  // enclosing Field's status → red text that matches the red input border.
  if (!slug) {
    return (
      <FieldHint>
        Use at least one letter or number so this column can be used as an email
        variable.
      </FieldHint>
    )
  }
  if (isReserved) {
    return (
      <FieldHint>
        This name would conflict with a standard email variable. Choose a more
        specific column name.
      </FieldHint>
    )
  }
  if (isDuplicate) {
    return (
      <FieldHint>
        A campaign column already uses this email variable. Choose a different
        name.
      </FieldHint>
    )
  }
  return (
    <FieldHint>
      Email variable:{' '}
      <code className="rounded bg-fbui-gray-100 px-1.5 py-0.5 font-mono text-xs text-fbui-gray-700">
        {`{${slug.toUpperCase()}}`}
      </code>
    </FieldHint>
  )
}

function AgentPreflightNotice({
  preflight,
  isLoading,
}: {
  preflight?: AgentPreflight
  isLoading: boolean
}) {
  if (isLoading && !preflight) {
    return (
      <Alert status="default" inline>
        <AlertIndicator />
        <AlertContent>
          <AlertDescription>Checking campaign readiness…</AlertDescription>
        </AlertContent>
      </Alert>
    )
  }
  if (!preflight || preflight.total_contacts === 0) return null

  if (preflight.missing_contacts === 0) {
    return (
      <Alert status="success" inline>
        <AlertIndicator />
        <AlertContent>
          <AlertDescription>
            All {preflight.total_contacts} contacts have the context this agent
            needs.
          </AlertDescription>
        </AlertContent>
      </Alert>
    )
  }

  return (
    <Alert status="warning">
      <AlertIndicator />
      <AlertContent>
        <AlertTitle>
          {preflight.missing_contacts} of {preflight.total_contacts} contacts
          may need manual values.
        </AlertTitle>
        <AlertDescription>
          Those rows are missing context this agent needs, so they may come back
          blank and require manual entry before using the variable in an email.
        </AlertDescription>
        {preflight.requirements.length > 0 && (
          <div className="mt-1 space-y-1">
            {preflight.requirements.map((requirement) => (
              <AlertDescription key={requirement.key}>
                <span className="font-medium">
                  {requirement.missing_count} missing {requirement.label}
                </span>
                <span>: {requirement.message}</span>
              </AlertDescription>
            ))}
          </div>
        )}
      </AlertContent>
    </Alert>
  )
}
